/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps, no-useless-escape */
// src/components/UncompletedJobs.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Accordion,
  Button,
  Spinner,
  Alert,
  Modal,
  Form,
  Row,
  Col
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useLeadNotifications from '../hooks/useLeadNotifications';
import JobCompletionModal from './JobCompletionModal';
import JobReviewModal from './JobReviewModal';
import DateTimePicker from './DateTimePicker';

// ── Helper functions ───────────────────────────────────
function formatWhen(iso) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    hour: 'numeric', minute: '2-digit', hour12: true,
    weekday: 'short', day: 'numeric', month: 'short',
    timeZone: 'Europe/London'
  }).formatToParts(new Date(iso));
  const map = Object.fromEntries(parts.map(p => [p.type, p.value]));
  return `${map.hour}:${map.minute} ${map.dayPeriod?.toLowerCase()}, ${map.weekday} ${map.day} ${map.month}`;
}

function extractTowns(from, to) {
  const town = loc => loc.split(',').slice(-2, -1)[0]?.trim() || loc;
  return `${town(from)} → ${town(to)}`;
}

function getCancellationReasonLabel(reason) {
  const labels = {
    'within15min': 'Within 15 minutes',
    'within30min': 'Within 30 minutes',
    'within1hr': 'Within 1 hour',
    'noShow': 'No Show',
    'abusive': 'Abusive',
    'inputError': 'Input Error',
    'trainBusCancellation': 'Train/Bus Cancellation',
    'duplicate': 'Duplicate',
    'noLongerRequired': 'No Longer Required',
    'test': 'Test',
    'other': 'Other'
  };
  return labels[reason] || reason;
}

const formatTimestamp = iso => {
  if (!iso) return 'Not available';
  const dt = new Date(iso);
  const hours = dt.getHours().toString().padStart(2, '0');
  const minutes = dt.getMinutes().toString().padStart(2, '0');
  const day = dt.getDate().toString().padStart(2, '0');
  const month = dt.toLocaleDateString('en-GB', { month: 'short', timeZone: 'Europe/London' });
  const year = dt.getFullYear().toString().slice(-2);
  return `${hours}:${minutes}-${day}/${month}/${year}`;
};

export default function UncompletedJobs() {

  // ── Fire up your socket listener too ──
  useLeadNotifications();

  // ── Navigation state ─────────────────────────────────
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');
  const navigate = useNavigate();
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);

  const navCollapse = () => {
    document.querySelectorAll('.navbar-collapse.show').forEach(el => el.classList.remove('show'));
    setAdminDropdownOpen(false);
  };

  const toggleAdminDropdown = (e) => {
    e.preventDefault();
    setAdminDropdownOpen(!adminDropdownOpen);
  };

  const logout = () => {
    localStorage.clear();
    navigate('/login');
  };

  // ── State ───────────────────────────────────────────
  const [jobs, setJobs]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [status, setStatus]           = useState('');
  const [editJob, setEditJob]         = useState(null);
  const [editForm, setEditForm]       = useState({});
  const [savingEdit, setSavingEdit]   = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [showCompletedMonthModal, setShowCompletedMonthModal] = useState(false);
  const [showCompletedYearModal, setShowCompletedYearModal] = useState(false);
  const [showCompletedHourModal, setShowCompletedHourModal] = useState(false);
  const [showCompletedMinuteModal, setShowCompletedMinuteModal] = useState(false);
  const [showCompletedAMPMModal, setShowCompletedAMPMModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [duplicateAction, setDuplicateAction] = useState(null); // { job, dateTime, from, to }
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [jobToComplete, setJobToComplete] = useState(null);
  const [completingJob, setCompletingJob] = useState(false);
  
  // Job Review Modal state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [jobToReview, setJobToReview] = useState(null);
  const [reviewingJob, setReviewingJob] = useState(false);

  // ── CSV helpers ──────────────────────────────────────
  const toCsv = (rows) => rows.map(r => r.map(v => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  }).join(',')).join('\n');

  // ── Wait and Return helper ────────────────────────────
  const calculateReturnTime = (pickupTime, waitHours) => {
    try {
      const pickup = new Date(pickupTime);
      const returnTime = new Date(pickup.getTime() + (waitHours * 60 * 60 * 1000));
      return returnTime.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      return 'Invalid time';
    }
  };

  const handleDownloadCsv = () => {
    const headers = ['Date','Time','From','To','Customer Name','Customer Phone','Customer Email','Passengers','Fare (£)','Payment Method','Notes','Status'];
    const fmtDate = (iso) => new Date(iso).toLocaleDateString('en-GB', { timeZone: 'Europe/London' });
    const fmtTime = (iso) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Europe/London' });
    // Use filteredJobs if search is active, otherwise use all jobs
    const jobsToExport = searchTerm.trim() ? filteredJobs : jobs;
    const rows = jobsToExport.map(j => [
      fmtDate(j.dateTime),
      fmtTime(j.dateTime),
      j.from || '',
      j.to || '',
      j.customerName || j.name || '',
      j.customerPhone || j.phone || '',
      j.customerEmail || j.email || '',
      j.passengers ?? '',
      j.fareAmount ?? '',
      j.paymentMethod || 'Cash',
      j.notes || '',
      j.status || ''
    ]);
    const csv = toCsv([headers, ...rows]);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
    a.href = url;
    a.download = `uncompleted-jobs-${todayStr}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleEmailCsv = async () => {
    try {
      // Send filtered job IDs if search is active, otherwise send empty array to get all jobs
      const jobIds = searchTerm.trim() ? filteredJobs.map(j => j._id) : [];
      await axios.post('/view-leads/uncompleted/email-csv', { jobIds });
      toast.success('CSV email queued. Check your inbox.');
    } catch (e) {
      console.error(e);
      toast.error('Failed to queue CSV email.');
    }
  };

  const toLocalInputValue = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const pad = n => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // ── Fetch uncompleted jobs ────────────────────────────
  useEffect(() => {
    axios.get('/view-leads/uncompleted')
      .then(({ data }) => {
        setJobs(data);
        setFilteredJobs(data);
      })
      .catch(err => {
        console.error(err);
        setStatus('Failed to load uncompleted jobs.');
      })
      .finally(() => setLoading(false));
  }, []);

  // ── Search functionality ────────────────────────────
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredJobs(jobs);
      return;
    }

    const filtered = jobs.filter(job => 
      job.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.customerPhone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.from?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.to?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formatWhen(job.dateTime).toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.status?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredJobs(filtered);
  }, [searchTerm, jobs]);

  // Handle scroll to control sticky header
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsHeaderSticky(scrollTop > 100); // Only sticky after scrolling 100px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ── Date buckets ────────────────────────────────────
  const now            = new Date();
  const startToday     = new Date(now.setHours(0,0,0,0));
  const startYesterday = new Date(startToday);
  startYesterday.setDate(startYesterday.getDate() - 1);
  const startWeekAgo   = new Date(startToday);
  startWeekAgo.setDate(startWeekAgo.getDate() - 7);

  const todayJobs     = filteredJobs.filter(j => new Date(j.dateTime) >= startToday);
  const yesterdayJobs = filteredJobs.filter(j => {
    const d = new Date(j.dateTime);
    return d >= startYesterday && d < startToday;
  });
  const lastWeekJobs  = filteredJobs.filter(j => {
    const d = new Date(j.dateTime);
    return d >= startWeekAgo && d < startYesterday;
  });
  const olderJobs     = filteredJobs.filter(j => new Date(j.dateTime) < startWeekAgo);

  const showJobTimestamps = (job) => {
    const timestamps = [
      `📅 Job Date/Time: ${formatTimestamp(job.dateTime)}`,
      `📝 Created: ${formatTimestamp(job.dateSubmitted)}`,
      job.updatedAt ? `✏️ Last Modified: ${formatTimestamp(job.updatedAt)}` : null,
      job.acceptedAt ? `✅ Accepted: ${formatTimestamp(job.acceptedAt)}` : null,
      job.reassignedAt ? `🔄 Reassigned: ${formatTimestamp(job.reassignedAt)}` : null,
      job.completedAt ? `🏁 Completed: ${formatTimestamp(job.completedAt)}` : null
    ].filter(Boolean);

    const timestampText = timestamps.join('\n');
    toast.info(timestampText, {
      position: "top-center",
      autoClose: 8000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      style: { whiteSpace: 'pre-line' }
    });
  };

  // ── Edit handlers ───────────────────────────────────
  const openEdit = job => {
    setEditJob(job);
    setEditForm({
      customerName: job.customerName || '',
      customerPhone: job.customerPhone || '',
      customerEmail: job.customerEmail || '',
      from: job.from || '',
      to: job.to || '',
      dateTime: job.dateTime ? toLocalInputValue(job.dateTime) : '',
      passengers: job.passengers || '',
      fareAmount: job.fareAmount ? `£${job.fareAmount}` : '',
      paymentMethod: job.paymentMethod || 'Cash',
      status: job.status || 'pending',
      dog: job.dog || false,
      disabled: job.disabled || false,
      notes: job.notes || ''
    });
  };
  const closeEdit = () => {
    setEditJob(null);
    setEditForm({});
  };
  const saveEdit = async () => {
    if (!editForm.fareAmount?.trim()) {
      return toast.error('Enter a valid fare.');
    }
    setSavingEdit(true);
    try {
      const updateData = {
        customerName: editForm.customerName.trim(),
        customerPhone: editForm.customerPhone.trim(),
        customerEmail: editForm.customerEmail.trim(),
        from: editForm.from.trim(),
        to: editForm.to.trim(),
        dateTime: new Date(editForm.dateTime).toISOString(),
        passengers: parseInt(editForm.passengers) || 1,
        fareAmount: editForm.fareAmount.trim().replace(/^£\s*/, ''),
        paymentMethod: editForm.paymentMethod,
        status: editForm.status,
        dog: editForm.dog,
        disabled: editForm.disabled,
        notes: editForm.notes.trim()
      };
      
      await axios.patch(
        `/view-leads/${editJob._id}/update`,
        updateData
      );
      
      setJobs(js =>
        js.map(j =>
          j._id === editJob._id
            ? { ...j, ...updateData }
            : j
        )
      );
      
      // Clear the edit form
      setEditForm({});
      toast.success('Job updated successfully!');
      closeEdit();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setSavingEdit(false);
    }
  };

  // ── Send Invoice handler ────────────────────────────
 const handleSendInvoice = async job => {
    setSendingInvoice(job._id);
    try {
      await axios.post('/view-leads/receipt', {
        jobId:         job._id,
        amount:        parseFloat(job.fareAmount),
        customerEmail: job.customerEmail,
        type:          'invoice'
      });
      toast.success('Invoice sent!');
    } catch (err) {
      console.error('Receipt error:', err.response?.data || err);
      toast.error(err.response?.data?.error || 'Failed to send invoice');
    } finally {
      setSendingInvoice(null);
    }
  };

  // ── Send Receipt handler ────────────────────────────
 const handleSendReceipt = async job => {
    setSendingInvoice(job._id);
    try {
      await axios.post('/view-leads/receipt', {
        jobId:         job._id,
        amount:        parseFloat(job.fareAmount),
        customerEmail: job.customerEmail,
        type:          'receipt'
      });
      toast.success('Receipt sent!');
    } catch (err) {
      console.error('Receipt error:', err.response?.data || err);
      toast.error(err.response?.data?.error || 'Failed to send receipt');
    } finally {
      setSendingInvoice(null);
    }
  };

  // ── Mark as Completed handler ────────────────────────────
  const handleMarkCompleted = (job) => {
    setJobToComplete(job);
    setShowCompletionModal(true);
  };

  // ── Submit job completion with review data ────────────────
  const handleSubmitCompletion = async (completionData) => {
    if (!jobToComplete) return;
    
    try {
      setCompletingJob(true);
      await axios.post(`/view-leads/complete/${jobToComplete._id}`, completionData);
      
      // Send SMS review link if requested
      if (completionData.sendSMSReview && jobToComplete.customerPhone) {
        const reviewUrl = `${window.location.origin}/review-driver/${jobToComplete._id}`;
        const smsMessage = `🚕 Thank you for using Taxi-Local!\n\nYour journey is complete. We hope you had a great experience!\n\nPlease rate your journey:\n${reviewUrl}\n\nYour feedback helps us improve! ⭐`;
        
        // Use mobile SMS or show desktop alert
        const cleanPhone = jobToComplete.customerPhone.replace(/[\s\-\(\)]/g, '');
        const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(navigator.userAgent);
        
        if (isMobile) {
          const smsLink = `sms:${cleanPhone}?body=${encodeURIComponent(smsMessage)}`;
          window.location.href = smsLink;
        } else {
          alert(`📱 SMS Review Link\n\nTo: ${cleanPhone}\n\nMessage:\n${smsMessage}\n\nSince you're on desktop, please use your phone to send this SMS.`);
        }
      }
      
      toast.success('Job marked as completed!');
      
      // Remove from the list since it's now completed
      setJobs(js => js.filter(j => j._id !== jobToComplete._id));
      setFilteredJobs(fj => fj.filter(j => j._id !== jobToComplete._id));
      
      // Close modal and reset state
      setShowCompletionModal(false);
      setJobToComplete(null);
    } catch (err) {
      console.error('Complete error:', err.response?.data || err);
      toast.error(err.response?.data?.error || 'Failed to mark as completed');
    } finally {
      setCompletingJob(false);
    }
  };

  // ── Handle job review for cancelled jobs ─────────
  const handleJobReview = async (reviewData) => {
    if (!jobToReview) return;
    
    try {
      setReviewingJob(true);
      
      // Submit review for the cancelled job
      await axios.post(`/view-leads/review/${jobToReview._id}`, {
        starRating: reviewData.starRating,
        reviewText: reviewData.reviewText,
        isBlacklisted: reviewData.isBlacklisted,
        blacklistReason: reviewData.blacklistReason
      });
      
      toast.success('Review saved successfully!');
      
      // Close modal
      setShowReviewModal(false);
      setJobToReview(null);
      
    } catch (error) {
      console.error('Error saving review:', error);
      toast.error('Failed to save review. Please try again.');
    } finally {
      setReviewingJob(false);
    }
  };

  // ── Undo Cancellation handler ────────────────────────────
  const handleUndoCancellation = async job => {
    try {
      const response = await axios.post(`/view-leads/undo-cancellation/${job._id}`);
      toast.success(response.data.message);
      // Remove from the list since it's now back in the diary
      setJobs(js => js.filter(j => j._id !== job._id));
      setFilteredJobs(fj => fj.filter(j => j._id !== job._id));
    } catch (err) {
      console.error('Undo cancellation error:', err.response?.data || err);
      toast.error(err.response?.data?.error || 'Failed to undo cancellation');
    }
  };

  // ── Render one job item ─────────────────────────────
  const renderItem = (job, idx, allJobs = [], sectionType = '') => {
    // Check if this is the first job of a new day (for Last Week and Older sections)
    const isFirstJobOfNewDay = (sectionType === 'lastWeek' || sectionType === 'older') && 
      allJobs.length > 0 && 
      idx > 0 && 
      allJobs[idx - 1] && 
      new Date(job.dateTime).toDateString() !== new Date(allJobs[idx - 1].dateTime).toDateString();

    return (
    <Accordion.Item 
      key={job._id} 
      eventKey={String(idx)}
      className={isFirstJobOfNewDay ? 'day-change-border' : ''}
      style={isFirstJobOfNewDay ? { borderTop: '3px solid #000' } : {}}
    >
      <Accordion.Header>
        <div className="d-flex flex-column w-100">
          <strong>{formatWhen(job.dateTime)}</strong>
          <small className="text-muted">
            {extractTowns(job.from, job.to)}
          </small>
          <small className="text-warning">
            Status: {job.status}
          </small>
        </div>
      </Accordion.Header>
      <Accordion.Body>
        <p><strong>Customer:</strong> {job.customerName}</p>
        <p><strong>Route:</strong> {extractTowns(job.from, job.to)}</p>
        <div className="d-flex align-items-center mb-2" style={{ whiteSpace: 'nowrap' }}>
          <span className="me-2">📞</span>
          <span className="flex-grow-1">{job.customerPhone}</span>
        </div>
        <div className="d-flex align-items-center mb-2" style={{ whiteSpace: 'nowrap' }}>
          <span className="me-2">📧</span>
          <span className="flex-grow-1">{job.customerEmail}</span>
        </div>
        <div className="d-flex justify-content-between align-items-center">
          <p className="mb-0"><strong>Passengers:</strong> {job.passengers}</p>
          <Button 
            variant="outline-info" 
            size="sm" 
            onClick={() => showJobTimestamps(job)}
            title="View Job Timestamps"
            className="rounded-circle"
            style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem', width: '1.5rem', height: '1.5rem', backgroundColor: '#ffc107', borderColor: '#ffc107', color: '#000' }}
          >
            i
          </Button>
        </div>
        <p><strong>Status:</strong> <span className="badge bg-warning">{job.status}</span></p>
        {job.cancellationReason && (
          <p><strong>Cancellation Reason:</strong> <span className="badge bg-danger">{getCancellationReasonLabel(job.cancellationReason)}</span></p>
        )}
        {(job.dog || job.disabled || job.notes) && (
          <div className="mt-2">
            {job.dog && <span className="badge bg-warning me-2">🐕 Dog/Pet</span>}
            {job.disabled && <span className="badge bg-info me-2">♿ Disabled Access</span>}
            {job.notes && (
              <div className="mt-2">
                <strong>Notes:</strong> <span className="text-muted">{job.notes}</span>
              </div>
            )}
          </div>
        )}
        <div className="row align-items-center mb-3">
          {/* Price on the left */}
          <div className="col-auto">
            <p className="mb-0">
              <strong>Price:</strong> £{job.fareAmount ?? '—'}
            </p>
          </div>
        </div>

        {/* Action Buttons - Organized in rows */}
        <div className="d-flex flex-column gap-2 mt-3 w-100">
          {/* Row 1: Edit, Mark Completed Buttons OR UNDO for cancelled jobs */}
          <div className="d-flex gap-2 w-100">
            {job.status === 'cancelled' ? (
              // Show UNDO and Review buttons for cancelled jobs
              <>
                <Button
                  variant="warning"
                  size="md"
                  className="flex-fill"
                  onClick={() => handleUndoCancellation(job)}
                >
                  <i className="bi bi-arrow-counterclockwise me-1"></i>
                  UNDO
                </Button>
                <Button
                  variant="info"
                  size="md"
                  className="flex-fill"
                  onClick={() => {
                    setJobToReview(job);
                    setShowReviewModal(true);
                  }}
                >
                  <i className="bi bi-star me-1"></i>
                  Review
                </Button>
              </>
            ) : (
              // Show normal buttons for other jobs
              <>
                <Button
                  variant="secondary"
                  size="md"
                  className="flex-fill"
                  onClick={() => openEdit(job)}
                >
                  <i className="bi bi-pencil-square me-1"></i>
                  Edit
                </Button>
                <Button
                  variant="success"
                  size="md"
                  className="flex-fill"
                  onClick={() => handleMarkCompleted(job)}
                >
                  <i className="bi bi-check-circle me-1"></i>
                  Mark Completed
                </Button>
                <Button
                  variant="info"
                  size="md"
                  className="flex-fill"
                  onClick={() => {
                    setJobToReview(job);
                    setShowReviewModal(true);
                  }}
                >
                  <i className="bi bi-star me-1"></i>
                  Review
                </Button>
              </>
            )}
          </div>

          {/* Row 2: +Tomorrow, +Next Week, +Next Month */}
          <div className="d-flex gap-1 w-100" style={{ maxWidth: '100%' }}>
            <Button size="sm" variant="outline-secondary" className="flex-fill" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }} onClick={()=>{
              const base = new Date(job.dateTime); const dt = new Date(base.getTime()+24*60*60*1000);
              setDuplicateAction({ 
                job, 
                dateTime: dt, 
                from: job.from, 
                to: job.to,
                waitAndReturn: job.waitAndReturn,
                waitHours: job.waitHours
              });
            }}>+Tomorrow</Button>
            <Button size="sm" variant="outline-secondary" className="flex-fill" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }} onClick={()=>{
              const base = new Date(job.dateTime); 
              const dt = new Date(base);
              dt.setDate(dt.getDate() + 7);
              setDuplicateAction({ 
                job, 
                dateTime: dt, 
                from: job.from, 
                to: job.to,
                waitAndReturn: job.waitAndReturn,
                waitHours: job.waitHours
              });
            }}>+Next Week</Button>
            <Button size="sm" variant="outline-secondary" className="flex-fill" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }} onClick={()=>{
              const d = new Date(job.dateTime); const next = new Date(d); next.setMonth(d.getMonth()+1);
              setDuplicateAction({ 
                job, 
                dateTime: next, 
                from: job.from, 
                to: job.to,
                waitAndReturn: job.waitAndReturn,
                waitHours: job.waitHours
              });
            }}>+Next Month</Button>
          </div>

          {/* Row 3: Receipt and Invoice buttons (only if fare amount exists) */}
          {job.fareAmount && (
            <div className="d-flex gap-2 w-100">
              <Button
                size="md"
                variant="info"
                className="flex-fill"
                onClick={() => handleSendReceipt(job)}
                disabled={sendingInvoice === job._id}
              >
                {sendingInvoice === job._id
                  ? <Spinner as="span" animation="border" size="sm" />
                  : <><i className="bi bi-receipt me-1"></i>Receipt</>}
              </Button>
              <Button
                size="md"
                variant="success"
                className="flex-fill"
                onClick={() => handleSendInvoice(job)}
                disabled={sendingInvoice === job._id}
              >
                {sendingInvoice === job._id
                  ? <Spinner as="span" animation="border" size="sm" />
                  : <><i className="bi bi-bank me-1"></i>Invoice</>}
              </Button>
            </div>
          )}
        </div>

      </Accordion.Body>
    </Accordion.Item>
    );
  };

  // ── Main render ─────────────────────────────────────
  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <Spinner animation="border" /> Loading uncompleted jobs…
      </div>
    );
  }

  return (
    <>
      <ToastContainer position="top-center" />
      <div className="container mt-0" style={{ marginTop: '120px', paddingTop: '80px' }}>
        {/* Fixed Header - Page Content Only */}
        <div className="position-fixed bg-white border-bottom shadow-sm" style={{ 
          zIndex: 1029,
          top: '50px', // Position below the main navbar
          left: '0',
          right: '0',
          paddingTop: '8px',
          paddingBottom: '0px',
          borderTop: '2px solid #f8f9fa'
        }}>
          <div className="container">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0">Uncompleted Jobs</h4>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <Button size="sm" variant="outline-primary" onClick={handleEmailCsv} title="Email CSV">📧</Button>
              </div>
            </div>
            
            {/* Search Bar below icons */}
            <div className="mt-2 position-relative" style={{ maxWidth: 500 }}>
              <Form.Control
                type="text"
                placeholder="Search by customer name, phone, email, route, notes, date, or status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ 
                  fontSize: '0.875rem',
                  paddingRight: '2rem' 
                }}
              />
              {searchTerm && (
                <Button
                  variant="link"
                  className="position-absolute top-50 end-0 translate-middle-y text-muted pe-3"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                  tabIndex={0}
                  style={{ fontSize: '1.5rem', lineHeight: 1, textDecoration: 'none' }}
                >
                  ×
                </Button>
              )}
            </div>
          </div>
        </div>

      {status && <Alert variant="info">{status}</Alert>}

      {todayJobs.length > 0 && <>
        <h6 className="bg-dark text-warning text-center">Today</h6>
        <Accordion flush>{todayJobs.map(renderItem)}</Accordion>
      </>}

      {yesterdayJobs.length > 0 && <>
        <h6 className="bg-dark text-warning text-center">Yesterday</h6>
        <Accordion flush>{yesterdayJobs.map(renderItem)}</Accordion>
      </>}

      {lastWeekJobs.length > 0 && <>
        <h6 className="bg-dark text-warning text-center">Last Week</h6>
        <Accordion flush>{lastWeekJobs.map((job, idx) => renderItem(job, idx, lastWeekJobs, 'lastWeek'))}</Accordion>
      </>}

      {olderJobs.length > 0 && <>
        <h6 className="bg-dark text-warning text-center">Older</h6>
        <Accordion flush>{olderJobs.map((job, idx) => renderItem(job, idx, olderJobs, 'older'))}</Accordion>
      </>}

      {filteredJobs.length === 0 && <p>No uncompleted jobs found.</p>}

      {/* Edit Job Modal */}
      <Modal show={!!editJob} onHide={closeEdit} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Uncompleted Job</Modal.Title>
          <Button variant="primary" onClick={saveEdit} disabled={savingEdit} className="ms-auto">
            {savingEdit
              ? <Spinner as="span" animation="border" size="sm" />
              : 'Save Changes'}
          </Button>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Date & Time Section */}
            <div className="border-bottom pb-2 mb-2">
              <h6 className="text-muted mb-2">📅 Journey Date</h6>
              <Row className="g-2">
                <Col xs={4}>
                  <Form.Label className="small mb-1">Month</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.dateTime ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][new Date(editForm.dateTime).getMonth()] : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][new Date().getMonth()]}
                    onClick={() => setShowCompletedMonthModal(true)}
                    readOnly
                    size="sm"
                    placeholder="Click to select month"
                    style={{ cursor: 'pointer' }}
                    disabled={savingEdit}
                  />
                </Col>
                <Col xs={4}>
                  <Form.Label className="small mb-1">Date</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.dateTime ? new Date(editForm.dateTime).getDate() : new Date().getDate()}
                    onClick={() => {/* TODO: Add date picker modal */}}
                    readOnly
                    size="sm"
                    placeholder="Click to select date"
                    style={{ cursor: 'pointer' }}
                    disabled={savingEdit}
                  />
                </Col>
                <Col xs={4}>
                  <Form.Label className="small mb-1">Year</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.dateTime ? new Date(editForm.dateTime).getFullYear() : new Date().getFullYear()}
                    onClick={() => setShowCompletedYearModal(true)}
                    readOnly
                    size="sm"
                    placeholder="Click to select year"
                    style={{ cursor: 'pointer' }}
                    disabled={savingEdit}
                  />
                </Col>
              </Row>
              <Row className="g-2 mt-0">
                <Col xs={4}>
                  <Form.Label className="small mb-1">Hour</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.dateTime ? (new Date(editForm.dateTime).getHours() % 12 || 12) : (new Date().getHours() % 12 || 12)}
                    onClick={() => setShowCompletedHourModal(true)}
                    readOnly
                    size="sm"
                    placeholder="Click to select hour"
                    style={{ cursor: 'pointer' }}
                    disabled={savingEdit}
                  />
                </Col>
                <Col xs={4}>
                  <Form.Label className="small mb-1">Minute</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.dateTime ? Math.floor(new Date(editForm.dateTime).getMinutes() / 5) * 5 : Math.floor(new Date().getMinutes() / 5) * 5}
                    onClick={() => setShowCompletedMinuteModal(true)}
                    readOnly
                    size="sm"
                    placeholder="Click to select minute"
                    style={{ cursor: 'pointer' }}
                    disabled={savingEdit}
                  />
                </Col>
                <Col xs={4}>
                  <Form.Label className="small mb-1">am/pm</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.dateTime ? (new Date(editForm.dateTime).getHours() < 12 ? 'am' : 'pm') : (new Date().getHours() < 12 ? 'am' : 'pm')}
                    onClick={() => setShowCompletedAMPMModal(true)}
                    readOnly
                    size="sm"
                    placeholder="Click to select am/pm"
                    style={{ cursor: 'pointer' }}
                    disabled={savingEdit}
                  />
                </Col>
              </Row>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Customer Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.customerName || ''}
                    onChange={e => setEditForm({...editForm, customerName: e.target.value})}
                    disabled={savingEdit}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Customer Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    value={editForm.customerPhone || ''}
                    onChange={e => setEditForm({...editForm, customerPhone: e.target.value})}
                    disabled={savingEdit}
                  />
                </Form.Group>
              </div>
            </div>
            
            <Form.Group className="mb-3">
              <Form.Label>Customer Email</Form.Label>
              <Form.Control
                type="email"
                value={editForm.customerEmail || ''}
                onChange={e => setEditForm({...editForm, customerEmail: e.target.value})}
                disabled={savingEdit}
              />
            </Form.Group>
            
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>From</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.from || ''}
                    onChange={e => setEditForm({...editForm, from: e.target.value})}
                    disabled={savingEdit}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>To</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.to || ''}
                    onChange={e => setEditForm({...editForm, to: e.target.value})}
                    disabled={savingEdit}
                  />
                </Form.Group>
              </div>
            </div>
            
            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Passengers</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={editForm.passengers || ''}
                    onChange={e => setEditForm({...editForm, passengers: e.target.value})}
                    disabled={savingEdit}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Fare (£)</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.fareAmount || ''}
                    onChange={e => setEditForm({...editForm, fareAmount: e.target.value})}
                    disabled={savingEdit}
                    placeholder="£0.00"
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Payment Method</Form.Label>
                  <Form.Select
                    value={editForm.paymentMethod || 'Cash'}
                    onChange={e => setEditForm({...editForm, paymentMethod: e.target.value})}
                    disabled={savingEdit}
                  >
                    <option value="Cash">Cash</option>
                    <option value="Card">Card</option>
                    <option value="BankTransfer">Bank Transfer</option>
                    <option value="Invoiced">Invoiced</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={editForm.status || 'pending'}
                    onChange={e => setEditForm({...editForm, status: e.target.value})}
                    disabled={savingEdit}
                  >
                    <option value="pending">Pending</option>
                    <option value="accepted">Accepted</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="dog"
                    label="🐕 Dog/Pet"
                    checked={editForm.dog || false}
                    onChange={e => setEditForm({...editForm, dog: e.target.checked})}
                    disabled={savingEdit}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="disabled"
                    label="♿ Disabled Access Required"
                    checked={editForm.disabled || false}
                    onChange={e => setEditForm({...editForm, disabled: e.target.checked})}
                    disabled={savingEdit}
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Notes (max 50 characters)</Form.Label>
              <Form.Control
                as="textarea"
                rows="2"
                value={editForm.notes || ''}
                onChange={e => setEditForm({...editForm, notes: e.target.value})}
                disabled={savingEdit}
                maxLength={50}
                placeholder="Any additional notes..."
              />
              <Form.Text className="text-muted">
                {editForm.notes ? editForm.notes.length : 0}/50 characters
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEdit} disabled={savingEdit}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveEdit} disabled={savingEdit} className="ms-auto">
            {savingEdit
              ? <Spinner as="span" animation="border" size="sm" />
              : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Duplicate Confirmation Modal */}
      <Modal show={!!duplicateAction} onHide={()=>setDuplicateAction(null)}>
        <Modal.Header closeButton><Modal.Title>Copy Job</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <DateTimePicker
              value={duplicateAction?.dateTime || new Date()}
              onChange={(newDateTime) => setDuplicateAction(a => ({ ...a, dateTime: newDateTime }))}
              label="Date & Time"
              className="mb-3"
              use24Hour={false}
              show24HourToggle={false}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">From</label>
            <input
              type="text"
              className="form-control"
              value={duplicateAction?.from || ''}
              onChange={e => setDuplicateAction(a => ({ ...a, from: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">To</label>
            <input
              type="text"
              className="form-control"
              value={duplicateAction?.to || ''}
              onChange={e => setDuplicateAction(a => ({ ...a, to: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="duplicateWaitAndReturn"
                checked={duplicateAction?.waitAndReturn || false}
                onChange={e => setDuplicateAction(a => ({ ...a, waitAndReturn: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="duplicateWaitAndReturn">
                Wait & Return
              </label>
            </div>
          </div>
          {duplicateAction?.waitAndReturn && (
            <div className="mb-3">
              <label className="form-label">Expected Return Time (hours)</label>
              <input
                type="number"
                className="form-control"
                min="0.5"
                max="24"
                step="0.5"
                value={duplicateAction?.waitHours || ''}
                onChange={e => setDuplicateAction(a => ({ ...a, waitHours: parseFloat(e.target.value) || null }))}
                placeholder="4.5"
              />
              <div className="form-text text-muted">
                {duplicateAction?.waitHours && duplicateAction?.dateTime ? 
                  `Return time: ${calculateReturnTime(duplicateAction.dateTime, duplicateAction.waitHours)}` : 
                  'Enter hours to wait before return'
                }
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={()=>setDuplicateAction(null)}>Cancel</Button>
          <Button variant="primary" onClick={async ()=>{
            try {
              await axios.post('/diary/add-entry', {
                customerName: duplicateAction.job.customerName,
                customerEmail: duplicateAction.job.customerEmail,
                customerPhone: duplicateAction.job.customerPhone,
                from: duplicateAction.from,
                to: duplicateAction.to,
                dateTime: duplicateAction.dateTime,
                passengers: duplicateAction.job.passengers,
                returnTrip: duplicateAction.job.returnTrip,
                dog: duplicateAction.job.dog,
                disabled: duplicateAction.job.disabled,
                notes: duplicateAction.job.notes,
                waitAndReturn: duplicateAction.waitAndReturn,
                waitHours: duplicateAction.waitHours,
              });
              toast.success('Job copied to diary');
            } catch (e) {
              toast.error('Failed to copy job');
            } finally {
              setDuplicateAction(null);
            }
          }}>Add to My Diary</Button>
        </Modal.Footer>
      </Modal>

      {/* Date/Time Picker Modals - Same as CompletedJobs */}
      <Modal show={showCompletedMonthModal} onHide={() => setShowCompletedMonthModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6 text-center w-100">Select Month</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <div className="d-flex flex-wrap gap-2">
            {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, index) => (
              <Button
                key={index}
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  const newDate = new Date(editForm.dateTime || new Date());
                  newDate.setMonth(index);
                  setEditForm(f => ({ ...f, dateTime: newDate.toISOString() }));
                  setShowCompletedMonthModal(false);
                }}
                className={`${editForm.dateTime && new Date(editForm.dateTime).getMonth() === index ? 'active' : ''}`}
              >
                {month}
              </Button>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={() => setShowCompletedMonthModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCompletedYearModal} onHide={() => setShowCompletedYearModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6 text-center w-100">Select Year</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <div className="d-flex flex-wrap gap-2">
            {[2025, 2026, 2027].map((year) => (
              <Button
                key={year}
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  const newDate = new Date(editForm.dateTime || new Date());
                  newDate.setFullYear(year);
                  setEditForm(f => ({ ...f, dateTime: newDate.toISOString() }));
                  setShowCompletedYearModal(false);
                }}
                className={`${editForm.dateTime && new Date(editForm.dateTime).getFullYear() === year ? 'active' : ''}`}
              >
                {year}
              </Button>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={() => setShowCompletedYearModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCompletedHourModal} onHide={() => setShowCompletedHourModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6 text-center w-100">Select Hour</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <div className="d-flex flex-wrap gap-2">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((hour) => (
              <Button
                key={hour}
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  const newDate = new Date(editForm.dateTime || new Date());
                  const currentHour = newDate.getHours();
                  const isPM = currentHour >= 12;
                  const newHour = isPM ? hour + 12 : hour;
                  if (hour === 12) {
                    newDate.setHours(isPM ? 12 : 0);
                  } else {
                    newDate.setHours(newHour);
                  }
                  setEditForm(f => ({ ...f, dateTime: newDate.toISOString() }));
                  setShowCompletedHourModal(false);
                }}
                className={`${editForm.dateTime && (new Date(editForm.dateTime).getHours() % 12 || 12) === hour ? 'active' : ''}`}
              >
                {hour}
              </Button>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={() => setShowCompletedHourModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCompletedMinuteModal} onHide={() => setShowCompletedMinuteModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6 text-center w-100">Select Minute</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <div className="d-flex flex-wrap gap-2">
            {Array.from({ length: 12 }, (_, i) => i * 5).map((minute) => (
              <Button
                key={minute}
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  const newDate = new Date(editForm.dateTime || new Date());
                  newDate.setMinutes(minute);
                  setEditForm(f => ({ ...f, dateTime: newDate.toISOString() }));
                  setShowCompletedMinuteModal(false);
                }}
                className={`${editForm.dateTime && Math.floor(new Date(editForm.dateTime).getMinutes() / 5) * 5 === minute ? 'active' : ''}`}
              >
                {minute.toString().padStart(2, '0')}
              </Button>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={() => setShowCompletedMinuteModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCompletedAMPMModal} onHide={() => setShowCompletedAMPMModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6 text-center w-100">Select am/pm</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <div className="d-flex flex-wrap gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                const newDate = new Date(editForm.dateTime || new Date());
                const currentHour = newDate.getHours();
                if (currentHour >= 12) {
                  newDate.setHours(currentHour - 12);
                }
                setEditForm(f => ({ ...f, dateTime: newDate.toISOString() }));
                setShowCompletedAMPMModal(false);
              }}
              className={`${editForm.dateTime && new Date(editForm.dateTime).getHours() < 12 ? 'active' : ''}`}
            >
              am
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                const newDate = new Date(editForm.dateTime || new Date());
                const currentHour = newDate.getHours();
                if (currentHour < 12) {
                  newDate.setHours(currentHour + 12);
                }
                setEditForm(f => ({ ...f, dateTime: newDate.toISOString() }));
                setShowCompletedAMPMModal(false);
              }}
              className={`${editForm.dateTime && new Date(editForm.dateTime).getHours() >= 12 ? 'active' : ''}`}
            >
              pm
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={() => setShowCompletedAMPMModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Job Completion Modal with Review System */}
      <JobCompletionModal
        show={showCompletionModal}
        onHide={() => {
          setShowCompletionModal(false);
          setJobToComplete(null);
        }}
        onSubmit={handleSubmitCompletion}
        job={jobToComplete}
        isSubmitting={completingJob}
      />

      {/* Job Review Modal for Cancelled Jobs */}
      <JobReviewModal
        show={showReviewModal}
        onHide={() => {
          setShowReviewModal(false);
          setJobToReview(null);
        }}
        onSubmit={handleJobReview}
        job={jobToReview}
        isSubmitting={reviewingJob}
      />

      </div>
    </>
  );
}
